import { baseApi } from './baseApi';

export interface Ticket {
  _id: string;
  title: string;
  priority: 'low' | 'medium' | 'high';
  category: 'numbers' | 'proxies' | 'billing' | 'other';
  status: 'open' | 'pending' | 'closed';
  user: any;
  assignedTo?: any;
  tags?: any[];
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface TicketMessage {
  _id: string;
  ticket: string;
  sender: any;
  senderType: 'user' | 'admin';
  content: string;
  hasAttachments: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TicketAttachment {
  _id: string;
  message: string;
  ticket: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  filePath: string;
  uploadedBy: any;
  createdAt: string;
  updatedAt: string;
}

export interface TicketInternalNote {
  _id: string;
  ticket: string;
  content: string;
  createdBy: any;
  createdAt: string;
  updatedAt: string;
}

export interface TicketStats {
  open: number;
  pending: number;
  closed: number;
  total: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const ticketApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // User endpoints
    createTicket: builder.mutation<Ticket, { title: string; priority: string; category: string; content: string; attachments?: FileList }>({
      query: (data) => {
        const formData = new FormData();
        formData.append('title', data.title);
        formData.append('priority', data.priority);
        formData.append('category', data.category);
        formData.append('content', data.content);
        if (data.attachments) {
          Array.from(data.attachments).forEach((file) => {
            formData.append('attachments', file);
          });
        }
        return {
          url: '/tickets',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Tickets'],
    }),

    getUserTickets: builder.query<PaginatedResponse<Ticket>, { status?: string; search?: string; sortBy?: string; page?: number; limit?: number }>({
      query: (params) => ({
        url: '/tickets',
        params,
      }),
      providesTags: ['Tickets'],
    }),

    getTicketById: builder.query<{ ticket: Ticket; messages: TicketMessage[]; attachments: TicketAttachment[] }, string>({
      query: (ticketId) => `/tickets/${ticketId}`,
      transformResponse: (response: { success: boolean; data: { ticket: Ticket; messages: TicketMessage[]; attachments: TicketAttachment[] } }) => response.data,
      providesTags: (result, error, ticketId) => [{ type: 'Ticket', id: ticketId }],
    }),

    sendMessage: builder.mutation<{ message: TicketMessage; attachments: TicketAttachment[] }, { ticketId: string; content: string; attachments?: FileList }>({
      query: ({ ticketId, content, attachments }) => {
        const formData = new FormData();
        formData.append('content', content);
        if (attachments) {
          Array.from(attachments).forEach((file) => {
            formData.append('attachments', file);
          });
        }
        return {
          url: `/tickets/${ticketId}/messages`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { ticketId }) => [{ type: 'Ticket', id: ticketId }, 'TicketMessages'],
    }),

    // Support admin endpoints
    getAllTickets: builder.query<PaginatedResponse<Ticket>, { status?: string; priority?: string; category?: string; search?: string; sortBy?: string; page?: number; limit?: number }>({
      query: (params) => ({
        url: '/support/tickets',
        params,
      }),
      providesTags: ['AllTickets'],
    }),

    getAdminTicketById: builder.query<{ ticket: Ticket; messages: TicketMessage[]; attachments: TicketAttachment[]; internalNotes: TicketInternalNote[] }, string>({
      query: (ticketId) => `/support/tickets/${ticketId}`,
      transformResponse: (response: { success: boolean; data: { ticket: Ticket; messages: TicketMessage[]; attachments: TicketAttachment[]; internalNotes: TicketInternalNote[] } }) => response.data,
      providesTags: (result, error, ticketId) => [{ type: 'AdminTicket', id: ticketId }],
    }),

    sendAdminMessage: builder.mutation<{ message: TicketMessage; attachments: TicketAttachment[] }, { ticketId: string; content: string; attachments?: FileList }>({
      query: ({ ticketId, content, attachments }) => {
        const formData = new FormData();
        formData.append('content', content);
        if (attachments) {
          Array.from(attachments).forEach((file) => {
            formData.append('attachments', file);
          });
        }
        return {
          url: `/support/tickets/${ticketId}/messages`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { ticketId }) => [{ type: 'AdminTicket', id: ticketId }],
    }),

    deleteTicketMessage: builder.mutation<void, string>({
      query: (messageId) => ({
        url: `/support/messages/${messageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminTicket'],
    }),

    updateTicketStatus: builder.mutation<Ticket, { ticketId: string; status: string }>({
      query: ({ ticketId, status }) => ({
        url: `/support/tickets/${ticketId}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (result, error, { ticketId }) => [{ type: 'AdminTicket', id: ticketId }, 'AllTickets', 'DashboardStats'],
    }),

    addInternalNote: builder.mutation<TicketInternalNote, { ticketId: string; content: string }>({
      query: ({ ticketId, content }) => ({
        url: `/support/tickets/${ticketId}/notes`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (result, error, { ticketId }) => [{ type: 'AdminTicket', id: ticketId }],
    }),

    deleteInternalNote: builder.mutation<void, string>({
      query: (noteId) => ({
        url: `/support/notes/${noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminTicket'],
    }),

    createTag: builder.mutation<any, { name: string; color?: string }>({
      query: (data) => ({
        url: '/support/tags',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Tags'],
    }),

    getAllTags: builder.query<any[], void>({
      query: () => '/support/tags',
      providesTags: ['Tags'],
    }),

    addTagToTicket: builder.mutation<Ticket, { ticketId: string; tagId: string }>({
      query: ({ ticketId, tagId }) => ({
        url: `/support/tickets/${ticketId}/tags/${tagId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { ticketId }) => [{ type: 'AdminTicket', id: ticketId }, 'AllTickets'],
    }),

    removeTagFromTicket: builder.mutation<Ticket, { ticketId: string; tagId: string }>({
      query: ({ ticketId, tagId }) => ({
        url: `/support/tickets/${ticketId}/tags/${tagId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { ticketId }) => [{ type: 'AdminTicket', id: ticketId }, 'AllTickets'],
    }),

    getDashboardStats: builder.query<TicketStats, void>({
      query: () => '/support/dashboard/stats',
      providesTags: ['DashboardStats'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateTicketMutation,
  useGetUserTicketsQuery,
  useGetTicketByIdQuery,
  useSendMessageMutation,
  useGetAllTicketsQuery,
  useGetAdminTicketByIdQuery,
  useSendAdminMessageMutation,
  useDeleteTicketMessageMutation,
  useUpdateTicketStatusMutation,
  useAddInternalNoteMutation,
  useDeleteInternalNoteMutation,
  useCreateTagMutation,
  useGetAllTagsQuery,
  useAddTagToTicketMutation,
  useRemoveTagFromTicketMutation,
  useGetDashboardStatsQuery,
} = ticketApi;
